import { DateTime } from 'luxon';
import { AllocationModel } from 'app/models/allocation.model';
import { RegimeModel, ScheduleEntryModel, PRESENT_STATUSES } from 'app/models/schedule.model';
import { buildAllocationTooltip } from 'app/shared/utils/allocation-tooltip.util';
import { periodInterval, periodsForRegime } from 'app/modules/shifts/list/shared/presence-slot.utils';

const MINUTES_PER_DAY = 1440;

export interface RegulatorTimelineBlock {
    blockKey: string;
    allocationId: number;
    userId: number;
    userName: string;
    initials: string;
    companyId: number;
    startedAt: string | Date;
    endedAt: string | Date | null;
    startPct: number;
    sizePct: number;
    lane: number;
    laneCount: number;
    isOpen: boolean;
    tooltip: string;
}

/** Bloco "previsto" (schedule_entry, sem shift_id — regime pessoal do regulador). */
export interface RegulatorPlannedBlock {
    blockKey: string;
    scheduleEntryId: number;
    userId: number;
    userName: string;
    initials: string;
    companyId: number;
    startPct: number;
    sizePct: number;
    lane: number;
    laneCount: number;
    tooltip: string;
}

export interface RegulatorCompanySection {
    companyId: number;
    companyName: string;
    blocks: RegulatorTimelineBlock[];
    laneCount: number;
    plannedBlocks: RegulatorPlannedBlock[];
    plannedLaneCount: number;
}

export interface RegulatorTimelineModel {
    sections: RegulatorCompanySection[];
    nowLeftPct: number | null;
}

function initialsFrom(name: string): string {
    const w = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!w.length) return '—';
    if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
    return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

function minutesOfDay(date: DateTime): number {
    return date.hour * 60 + date.minute + date.second / 60;
}

/** Recorta o intervalo [start,end) da alocação na janela do dia informado (0-1440min). */
function clipToDay(startedAt: DateTime, endedAt: DateTime | null, dayStart: DateTime, now: DateTime): { start: number; end: number } | null {
    const dayEnd = dayStart.plus({ days: 1 });
    const effectiveEnd = endedAt ?? now;
    if (effectiveEnd <= dayStart || startedAt >= dayEnd) return null;
    const clippedStart = startedAt < dayStart ? dayStart : startedAt;
    const clippedEnd = effectiveEnd > dayEnd ? dayEnd : effectiveEnd;
    return {
        start: minutesOfDay(clippedStart),
        end: clippedStart.hasSame(clippedEnd, 'day') ? minutesOfDay(clippedEnd) : MINUTES_PER_DAY,
    };
}

function assignLanes(blocks: RegulatorTimelineBlock[]): number {
    const sorted = [...blocks].sort((a, b) => a.startPct - b.startPct);
    const lanes: { end: number }[] = [];
    for (const block of sorted) {
        const end = block.startPct + block.sizePct;
        let lane = lanes.findIndex((l) => l.end <= block.startPct + 0.05);
        if (lane < 0) {
            lane = lanes.length;
            lanes.push({ end });
        } else {
            lanes[lane].end = end;
        }
        block.lane = lane;
    }
    const laneCount = Math.max(1, lanes.length);
    for (const block of blocks) block.laneCount = laneCount;
    return laneCount;
}

/**
 * Linha do tempo do dia (00h-23h) das alocações reais dos médicos reguladores,
 * segmentada por empresa do turno — um regulador pode atender mais de uma.
 */
export function buildRegulatorTimeline(allocations: AllocationModel[], dateKey: string, timezone: string): RegulatorTimelineModel {
    const dayStart = DateTime.fromISO(dateKey, { zone: timezone }).startOf('day');
    const now = DateTime.now().setZone(timezone);
    const isToday = dayStart.hasSame(now, 'day');

    const sectionsByCompany = new Map<number, RegulatorCompanySection>();

    for (const allocation of allocations) {
        if (!allocation.started_at || allocation.company_id == null) continue;
        const startedAt = DateTime.fromJSDate(new Date(allocation.started_at)).setZone(timezone);
        const endedAt = allocation.ended_at ? DateTime.fromJSDate(new Date(allocation.ended_at)).setZone(timezone) : null;
        const clipped = clipToDay(startedAt, endedAt, dayStart, now);
        if (!clipped) continue;

        let section = sectionsByCompany.get(allocation.company_id);
        if (!section) {
            section = {
                companyId: allocation.company_id,
                companyName: allocation.company?.name?.trim() || `Empresa #${allocation.company_id}`,
                blocks: [],
                laneCount: 1,
                plannedBlocks: [],
                plannedLaneCount: 1,
            };
            sectionsByCompany.set(allocation.company_id, section);
        }

        const startPct = (clipped.start / MINUTES_PER_DAY) * 100;
        const sizePct = Math.max(((clipped.end - clipped.start) / MINUTES_PER_DAY) * 100, 0.3);
        const isOpen = !allocation.ended_at;

        for (const au of allocation.allocation_users || []) {
            if (!au.user?.is_regulator) continue;
            const name = au.user?.name || `Usuário #${au.user_id}`;
            const tooltip = [
                buildAllocationTooltip(name, allocation.started_at, allocation.ended_at, timezone, now.toJSDate()),
                section.companyName,
            ].join('\n');
            section.blocks.push({
                blockKey: `${allocation.allocation_id}|${au.user_id}`,
                allocationId: allocation.allocation_id,
                userId: au.user_id,
                userName: name,
                initials: initialsFrom(name),
                companyId: allocation.company_id,
                startedAt: allocation.started_at!,
                endedAt: allocation.ended_at,
                startPct,
                sizePct,
                lane: 0,
                laneCount: 1,
                isOpen,
                tooltip,
            });
        }
    }

    const sections = [...sectionsByCompany.values()].sort((a, b) => a.companyName.localeCompare(b.companyName, 'pt-BR'));
    for (const section of sections) {
        section.laneCount = assignLanes(section.blocks);
    }

    return {
        sections,
        nowLeftPct: isToday ? (minutesOfDay(now) / MINUTES_PER_DAY) * 100 : null,
    };
}

/**
 * Preenche a linha "Previsto" de cada seção (empresa) a partir do schedule_entry
 * dos reguladores no dia — sem shift_id, horário resolvido pelo regime pessoal.
 * Mutação in-place do model retornado por buildRegulatorTimeline.
 */
export function attachRegulatorPlannedBlocks(
    model: RegulatorTimelineModel,
    entries: ScheduleEntryModel[],
    regimes: RegimeModel[],
    companiesById: Map<number, string>,
): void {
    const regimeById = new Map((regimes || []).filter((r) => r.regime_id != null).map((r) => [r.regime_id!, r]));
    const sectionsByCompany = new Map(model.sections.map((s) => [s.companyId, s]));

    for (const entry of entries || []) {
        if (!PRESENT_STATUSES.includes(entry.status)) continue;
        if (entry.regime_id == null || entry.company_id == null) continue;
        const regime = regimeById.get(entry.regime_id);
        if (!regime) continue;

        const periods = periodsForRegime(regime);
        const period =
            (entry.regime_period_id != null && periods.find((p) => p.regime_period_id === entry.regime_period_id)) ||
            periods.find((p) => (p.slot ?? 0) === (entry.regime_slot ?? 0)) ||
            periods[0];
        if (!period) continue;
        const interval = periodInterval(period);
        if (!interval) continue;

        let section = sectionsByCompany.get(entry.company_id);
        if (!section) {
            section = {
                companyId: entry.company_id,
                companyName: companiesById.get(entry.company_id) || `Empresa #${entry.company_id}`,
                blocks: [],
                laneCount: 1,
                plannedBlocks: [],
                plannedLaneCount: 1,
            };
            sectionsByCompany.set(entry.company_id, section);
            model.sections.push(section);
        }

        const start = interval.start % MINUTES_PER_DAY;
        const end = Math.min(MINUTES_PER_DAY, start + (interval.end - interval.start));
        if (end <= start) continue;

        const userName = entry.user?.name || `Usuário #${entry.user_id}`;
        const startPct = (start / MINUTES_PER_DAY) * 100;
        const sizePct = Math.max(((end - start) / MINUTES_PER_DAY) * 100, 0.3);

        section.plannedBlocks.push({
            blockKey: `planned|${entry.schedule_entry_id ?? `${entry.user_id}|${entry.date}`}`,
            scheduleEntryId: entry.schedule_entry_id ?? 0,
            userId: entry.user_id,
            userName,
            initials: initialsFrom(userName),
            companyId: entry.company_id,
            startPct,
            sizePct,
            lane: 0,
            laneCount: 1,
            tooltip: `Previsto: ${userName}\n${section.companyName}`,
        });
    }

    model.sections.sort((a, b) => a.companyName.localeCompare(b.companyName, 'pt-BR'));
    for (const section of model.sections) {
        section.plannedLaneCount = assignPlannedLanes(section.plannedBlocks);
    }
}

function assignPlannedLanes(blocks: RegulatorPlannedBlock[]): number {
    const sorted = [...blocks].sort((a, b) => a.startPct - b.startPct);
    const lanes: { end: number }[] = [];
    for (const block of sorted) {
        const end = block.startPct + block.sizePct;
        let lane = lanes.findIndex((l) => l.end <= block.startPct + 0.05);
        if (lane < 0) {
            lane = lanes.length;
            lanes.push({ end });
        } else {
            lanes[lane].end = end;
        }
        block.lane = lane;
    }
    const laneCount = Math.max(1, lanes.length);
    for (const block of blocks) block.laneCount = laneCount;
    return laneCount;
}
